/**
 * Badania i BHP — widok HR (spec 01 §15).
 *
 * Stan liczy serwer jedną regułą (fala 8, grupy 26–28, ADR-0058): rekord
 * rozstrzygający zamiast „ostatniego wiersza", skierowanie w toku osobno
 * z terminem, instruktaż jednorazowy bez daty jako zaliczony. Tabela nie może
 * pokazać zieleni tam, gdzie orzeczenia nie ma [D19], ani czerwieni tam, gdzie
 * jest ważne. Wygasłe orzeczenie przy skierowaniu w toku jest czerwone —
 * art. 229 § 4 k.p. (D110).
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  ShieldPlus,
  Spinner,
  StatusPill,
  useToast,
} from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import type { HealthState } from '@k1p4/shared'
import { useHasPermission } from '../../auth/AuthProvider'
import { api, apiDay, ApiError, type HealthRecordItem } from '../../api/client'
import styles from './Health.module.css'

const TONE: Record<HealthState, 'success' | 'warning' | 'danger' | 'neutral'> = {
  ok: 'success',
  warn: 'warning',
  err: 'danger',
  todo: 'warning',
  na: 'neutral',
  missing: 'warning',
}

/** Zamknięcie skierowania: wynik badania albo ukończenie szkolenia (grupa 27, D104). */
interface ResultDraft {
  readonly row: HealthRecordItem
  readonly area: 'medical' | 'bhp'
  readonly id: string
  readonly bhpKind: string | null
  readonly date: string
  readonly validUntil: string
  readonly conductedBy: string
}

function referralErrorKey(cause: unknown): string {
  if (cause instanceof ApiError && cause.code === 'IN_ONBOARDING') return 'hrHealth.errorInOnboarding'
  if (cause instanceof ApiError && cause.code === 'RESULT_AWAITING_APPROVAL') return 'hrHealth.errorAwaitingApproval'
  return 'hrHealth.errorGeneric'
}

function resultErrorKey(cause: unknown): string {
  if (cause instanceof ApiError && cause.code === 'NOT_PENDING') return 'hrHealth.result.errorNotPending'
  if (cause instanceof ApiError && cause.code === 'IN_ONBOARDING') return 'hrHealth.result.errorInOnboarding'
  if (cause instanceof ApiError && cause.code === 'VALID_UNTIL_REQUIRED') return 'hrHealth.result.errorValidUntil'
  return 'hrHealth.result.errorGeneric'
}

export function Health() {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    kind: useId(),
    deadline: useId(),
    date: useId(),
    validUntil: useId(),
    conductedBy: useId(),
  }
  /* Skierowanie i wynik zapisują tylko role z „Badania : Edycja" — inaczej klik kończyłby się 403 [D28]. */
  const canEdit = useHasPermission('Badania : Edycja')

  const [referring, setReferring] = useState<HealthRecordItem | null>(null)
  const [form, setForm] = useState({ kind: 'medical' as 'medical' | 'bhp', deadlineDays: '14' })
  const [result, setResult] = useState<ResultDraft | null>(null)
  const [error, setError] = useState<string | null>(null)

  const records = useQuery({ queryKey: ['health-records'], queryFn: () => api.healthRecords() })

  const refer = useMutation({
    mutationFn: () =>
      api.referHealth(referring!.employeeId, {
        kind: form.kind,
        deadlineDays: Number(form.deadlineDays),
      }),
    onSuccess: (outcome) => {
      toast.show(
        t(outcome.renewed ? 'hrHealth.referRenewed' : 'hrHealth.referred', {
          date: formatDate(outcome.dueOn, 'dot'),
        }),
        { tone: 'success' },
      )
      setReferring(null)
      void queryClient.invalidateQueries({ queryKey: ['health-records'] })
    },
    onError: (cause) => setError(referralErrorKey(cause)),
  })

  const save = useMutation({
    mutationFn: (draft: ResultDraft) =>
      draft.area === 'medical'
        ? api.recordMedicalResult(draft.id, { examDate: draft.date, validUntil: draft.validUntil })
        : api.completeBhpTraining(draft.id, {
            performedAt: draft.date,
            conductedBy: draft.conductedBy.trim(),
            ...(draft.validUntil === '' ? {} : { validUntil: draft.validUntil }),
          }),
    onSuccess: () => {
      toast.show(t('hrHealth.result.saved'), { tone: 'success' })
      setResult(null)
      void queryClient.invalidateQueries({ queryKey: ['health-records'] })
    },
    onError: (cause) => setError(resultErrorKey(cause)),
  })

  /*
   * Serwer waliduje termin jako 1–90 dni; atrybuty min/max na inpucie nie
   * powstrzymują wpisania ręcznie — przycisk musi znać ten sam warunek,
   * inaczej klik kończyłby się 400 bez czytelnej przyczyny.
   */
  const deadlineNumber = Number(form.deadlineDays)
  const deadlineValid =
    form.deadlineDays.trim() !== '' &&
    Number.isInteger(deadlineNumber) &&
    deadlineNumber >= 1 &&
    deadlineNumber <= 90

  if (records.isPending) return <Spinner label={t('common.loading')} />
  if (records.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const { items, counts } = records.data

  function openResult(row: HealthRecordItem, area: 'medical' | 'bhp'): void {
    const pending = area === 'medical' ? row.medical.pending : row.bhp.pending
    if (pending === null) return
    setError(null)
    setResult({
      row,
      area,
      id: pending.id,
      bhpKind: area === 'bhp' ? row.bhp.pending!.kind : null,
      date: apiDay(),
      validUntil: '',
      conductedBy: '',
    })
  }

  function pendingMeta(area: 'medical' | 'bhp', row: HealthRecordItem): string | null {
    const pending = area === 'medical' ? row.medical.pending : row.bhp.pending
    if (pending === null) return null
    if (pending.dueOn === null) return t('hrHealth.pendingNoDeadline')
    return area === 'medical'
      ? t('hrHealth.pendingMedical', { date: formatDate(pending.dueOn, 'dot') })
      : t('hrHealth.pendingBhp', {
          kind: t(`portal.health.kinds.${row.bhp.pending!.kind}`),
          date: formatDate(pending.dueOn, 'dot'),
        })
  }

  function cell(area: 'medical' | 'bhp', row: HealthRecordItem) {
    const data = area === 'medical' ? row.medical : row.bhp
    const meta = pendingMeta(area, row)
    return (
      <td>
        <StatusPill tone={TONE[data.validity]}>{t(`hrHealth.validity.${data.validity}`)}</StatusPill>
        <span className={styles.meta}>
          {data.validUntil
            ? t('hrHealth.validUntil', { date: formatDate(data.validUntil, 'dot') })
            : data.validity === 'missing'
              ? t('hrHealth.missingHint')
              : data.validity === 'todo' && row.onboarding
                ? t('hrHealth.inOnboarding')
                : t('hrHealth.noDate')}
        </span>
        {meta !== null ? <span className={styles.meta}>{meta}</span> : null}
        {meta !== null && canEdit && !row.onboarding ? (
          <Button size="sm" variant="ghost" onClick={() => openResult(row, area)}>
            {t('hrHealth.result.open')}
          </Button>
        ) : null}
      </td>
    )
  }

  const resultValid =
    result !== null &&
    result.date !== '' &&
    result.date <= apiDay() &&
    (result.area === 'medical'
      ? result.validUntil > result.date
      : result.conductedBy.trim() !== '' && (result.validUntil === '' || result.validUntil > result.date))

  return (
    <>
      <p className={styles.lead}>{t('hrHealth.lead')}</p>

      {error !== null && referring === null && result === null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {items.length === 0 ? (
        <EmptyState
          icon={<ShieldPlus size={28} />}
          title={t('hrHealth.empty')}
          description={t('hrHealth.emptyBody')}
        />
      ) : (
        <>
          <p className={styles.counts}>
            {t('hrHealth.counts', { all: counts.all, warn: counts.warn, err: counts.err })}
          </p>

          <Card pad="flush" className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('hrHealth.columns.employee')}</th>
                  <th>{t('hrHealth.columns.medical')}</th>
                  <th>{t('hrHealth.columns.bhp')}</th>
                  <th aria-label={t('hrHealth.refer')} />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.employeeId}>
                    <td>
                      <span className={styles.name}>
                        {row.firstName} {row.lastName}
                      </span>
                      <span className={styles.meta}>
                        {row.displayId}
                        {row.position ? ` · ${row.position}` : ''}
                      </span>
                    </td>
                    {cell('medical', row)}
                    {cell('bhp', row)}
                    <td className={styles.actionCell}>
                      {/* W procesie zatrudnienia skierowanie wystawia proces (grupa 27). */}
                      {canEdit && !row.onboarding ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setError(null)
                            setReferring(row)
                          }}
                        >
                          {t('hrHealth.refer')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      <Modal
        open={referring !== null}
        onClose={() => setReferring(null)}
        title={t('hrHealth.referTitle')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setReferring(null)}>
              {t('hrLeaves.cancel')}
            </Button>
            <Button disabled={refer.isPending || !deadlineValid} onClick={() => refer.mutate()}>
              {t('hrHealth.referSubmit')}
            </Button>
          </div>
        }
      >
        {/* Overlay modala zakrywa baner strony — błąd musi być widoczny TUTAJ. */}
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        {referring ? (
          <p className={styles.modalSummary}>
            {referring.firstName} {referring.lastName}
          </p>
        ) : null}
        <FormField label={t('hrHealth.referKind')} htmlFor={ids.kind}>
          <Select
            id={ids.kind}
            value={form.kind}
            onChange={(event) =>
              setForm({ ...form, kind: event.target.value as 'medical' | 'bhp' })
            }
          >
            <option value="medical">{t('hrHealth.referMedical')}</option>
            <option value="bhp">{t('hrHealth.referBhp')}</option>
          </Select>
        </FormField>
        <FormField label={t('hrHealth.referDeadline')} htmlFor={ids.deadline}>
          <Input
            id={ids.deadline}
            type="number"
            min={1}
            max={90}
            value={form.deadlineDays}
            onChange={(event) => setForm({ ...form, deadlineDays: event.target.value })}
          />
        </FormField>
      </Modal>

      <Modal
        open={result !== null}
        onClose={() => setResult(null)}
        title={t(result?.area === 'bhp' ? 'hrHealth.result.titleBhp' : 'hrHealth.result.titleMedical')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setResult(null)}>
              {t('hrLeaves.cancel')}
            </Button>
            <Button disabled={save.isPending || !resultValid} onClick={() => save.mutate(result!)}>
              {t('hrHealth.result.save')}
            </Button>
          </div>
        }
      >
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        {result ? (
          <>
            <p className={styles.modalSummary}>
              {result.row.firstName} {result.row.lastName}
              {result.bhpKind ? ` · ${t(`portal.health.kinds.${result.bhpKind}`)}` : ''}
            </p>
            <FormField
              label={t(result.area === 'medical' ? 'hrHealth.result.examDate' : 'hrHealth.result.performedAt')}
              htmlFor={ids.date}
            >
              <Input
                id={ids.date}
                type="date"
                max={apiDay()}
                value={result.date}
                onChange={(event) => setResult({ ...result, date: event.target.value })}
              />
            </FormField>
            {result.area === 'bhp' ? (
              <FormField label={t('hrHealth.result.conductedBy')} htmlFor={ids.conductedBy}>
                <Input
                  id={ids.conductedBy}
                  value={result.conductedBy}
                  onChange={(event) => setResult({ ...result, conductedBy: event.target.value })}
                />
              </FormField>
            ) : null}
            <FormField
              label={t(result.area === 'medical' ? 'hrHealth.result.validUntil' : 'hrHealth.result.bhpValidUntil')}
              htmlFor={ids.validUntil}
              {...(result.area === 'bhp' ? { hint: t('hrHealth.result.bhpValidUntilHint') } : {})}
            >
              <Input
                id={ids.validUntil}
                type="date"
                value={result.validUntil}
                onChange={(event) => setResult({ ...result, validUntil: event.target.value })}
              />
            </FormField>
          </>
        ) : null}
      </Modal>
    </>
  )
}
